//@ts-nocheck
import React, { useEffect, useState, useRef } from "react";
import { PowerBIEmbed } from "powerbi-client-react";
import { models } from "powerbi-client";
import { getReportEmbedInfo } from "../services/powerBiService";
interface PowerBIReportProps {
  style?: React.CSSProperties;
  isMobile: boolean;
  role: string;
  groupId: string;
  propsReportId: string;
  datasetId: string;
}

// 添加 isMobile 作为组件属性
function PowerBIReport({
  style,
  isMobile,
  role,
  groupId,
  propsReportId,
  datasetId,
}: PowerBIReportProps) {
  const [embedToken, setEmbedToken] = useState(null);
  const [embedUrl, setEmbedUrl] = useState(null);
  const [reportId, setReportId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const embedRef = useRef<any>(null);
  const [currentViewMode, setCurrentViewMode] = useState(models.ViewMode.View);
  const [hasMobileLayout, setHasMobileLayout] = useState(false);
  const [layoutCheckComplete, setLayoutCheckComplete] = useState(false);
  const [reportPages, setReportPages] = useState([]);
  const [currentPage, setCurrentPage] = useState(null);
  const lastLayoutTypeRef = useRef<any>(null);

  // 修改监听isMobile变化的useEffect
  useEffect(() => {
    // 更新视图模式状态
    setCurrentViewMode(models.ViewMode.View);

    // 如果报表已经加载并且已完成布局检查，则应用新的布局类型
    if (embedRef.current && embedRef.current.embed && layoutCheckComplete) {
      try {
        // 计算当前应该使用的布局类型
        const targetLayoutType = isMobile
          ? hasMobileLayout
            ? models.LayoutType.MobilePortrait
            : models.LayoutType.MobileLandscape
          : models.LayoutType.Master;

        // 检查是否需要更新布局类型（与上次不同）
        if (lastLayoutTypeRef.current !== targetLayoutType) {
          console.log(`切换到${isMobile ? "移动" : "桌面"}布局`);

          // 更新报表设置
          embedRef.current.embed
            .updateSettings({
              layoutType: targetLayoutType,
              panes: {
                pageNavigation: {
                  visible: true,
                  position: models.PageNavigationPosition.Bottom,
                },
              },
            })
            .then(() => {
              console.log(`布局切换成功`);
              // 记录当前应用的布局类型
              lastLayoutTypeRef.current = targetLayoutType;

              // 不需要在这里设置currentPage，而是在pageChanged事件处理
            })
            .catch((err: any) => {
              console.error("布局切换失败:", err);
            });
        }
      } catch (error) {
        console.error("切换布局失败:", error);
      }
    }
  }, [isMobile, layoutCheckComplete, hasMobileLayout]); // 移除 currentPage 依赖

  // 为PowerBI嵌入添加强制样式覆盖
  useEffect(() => {
    const addCSSOverride = () => {
      const styleElement = document.createElement("style");
      styleElement.innerHTML = `
                .powerbi-embed-component, 
                .powerbi-embed-component > div,
                .powerbi-embed-component > div > iframe,
                .powerbi-embed-component iframe {
                    width: 100% !important;
                    height: 100% !important;
                    min-height: 500px !important; /* 设置最小高度 */
                    position: absolute !important; /* 使用绝对定位 */
                    top: 0 !important;
                    left: 0 !important;
                    right: 0 !important;
                    bottom: 0 !important;
                    border: none !important;
                }
                
                /* 确保父容器也具有正确的尺寸 */
                .report-container {
                    position: relative !important;
                    height: 100% !important;
                    width: 100% !important;
                }
                
                /* 添加滑动指示器样式 */
                .swipe-indicator {
                    position: absolute;
                    top: 50%;
                    transform: translateY(-50%);
                    background-color: rgba(0,0,0,0.3);
                    color: white;
                    padding: 10px;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    width: 40px;
                    height: 40px;
                    font-size: 20px;
                    opacity: 0;
                    transition: opacity 0.3s;
                    pointer-events: none;
                    z-index: 1001;
                }
                
                .swipe-indicator.left {
                    left: 10px;
                }
                
                .swipe-indicator.right {
                    right: 10px;
                }
                
                .swipe-indicator.visible {
                    opacity: 1;
                    animation: fade-out 1s forwards;
                }
                
                @keyframes fade-out {
                    0% { opacity: 1; }
                    70% { opacity: 1; }
                    100% { opacity: 0; }
                }
                
                /* 移动端导航样式 */
                .mobile-nav {
                    display: flex;
                    overflow-x: auto;
                    background-color: rgba(255,255,255,0.7);
                    padding: 8px;
                    gap: 8px;
                    scrollbar-width: none;
                    -ms-overflow-style: none;
                }
                
                .mobile-nav::-webkit-scrollbar {
                    display: none;
                }
                
                .mobile-nav-button {
                    padding: 8px 12px;
                    border-radius: 16px;
                    border: none;
                    white-space: nowrap;
                    font-size: 12px;
                    cursor: pointer;
                    transition: background-color 0.2s, color 0.2s;
                }
                
                .mobile-nav-button.active {
                    background-color: #0078d4;
                    color: white;
                }
                
                .mobile-nav-button:not(.active) {
                    background-color: #f0f0f0;
                    color: #333;
                }
            `;
      document.head.appendChild(styleElement);
      return () => document.head.removeChild(styleElement);
    };

    const cleanup = addCSSOverride();
    return cleanup;
  }, []);

  // 添加滑动事件处理
  useEffect(() => {
    // 只在移动视图下添加滑动处理
    if (!isMobile || !embedRef.current) return;

    let touchStartX = 0;
    let touchEndX = 0;
    const minSwipeDistance = 50;

    const handleTouchStart = (e) => {
      touchStartX = e.changedTouches[0].screenX;
    };

    const handleTouchEnd = (e) => {
      touchEndX = e.changedTouches[0].screenX;
      handleSwipe();
    };

    const handleSwipe = async () => {
      const swipeDistance = touchEndX - touchStartX;

      // 确保有足够的滑动距离
      if (Math.abs(swipeDistance) < minSwipeDistance) return;

      try {
        // 获取当前所有页面和活动页面
        if (!embedRef.current || !embedRef.current.embed) return;

        const pages = await embedRef.current.embed.getPages();
        if (!pages || pages.length <= 1) return;

        const activePage = await embedRef.current.embed.getActivePage();
        const activeIndex = pages.findIndex((p) => p.name === activePage.name);

        // 根据滑动方向切换页面
        if (swipeDistance > 0 && activeIndex > 0) {
          // 向右滑动，前一页
          showSwipeIndicator("left");
          await pages[activeIndex - 1].setActive();
        } else if (swipeDistance < 0 && activeIndex < pages.length - 1) {
          // 向左滑动，后一页
          showSwipeIndicator("right");
          await pages[activeIndex + 1].setActive();
        }
      } catch (err) {
        console.error("滑动切换页面失败:", err);
      }
    };

    const showSwipeIndicator = (direction: any) => {
      const existingIndicator = document.querySelector(
        `.swipe-indicator.${direction}`
      );
      if (existingIndicator) {
        existingIndicator.classList.remove("visible");
        void existingIndicator.offsetWidth; // 重置动画
      }

      const indicator = existingIndicator || document.createElement("div");
      indicator.className = `swipe-indicator ${direction}`;
      indicator.innerText = direction === "left" ? "←" : "→";

      if (!existingIndicator) {
        document.querySelector(".report-container").appendChild(indicator);
      }

      // 触发动画
      setTimeout(() => {
        indicator.classList.add("visible");
      }, 10);

      // 动画结束后移除
      setTimeout(() => {
        if (indicator.parentNode) {
          indicator.parentNode.removeChild(indicator);
        }
      }, 1500);
    };

    // 在报表容器上添加触摸事件监听
    const reportContainer = document.querySelector(".report-container");
    if (reportContainer) {
      reportContainer.addEventListener("touchstart", handleTouchStart, {
        passive: true,
      });
      reportContainer.addEventListener("touchend", handleTouchEnd, {
        passive: true,
      });

      return () => {
        reportContainer.removeEventListener("touchstart", handleTouchStart);
        reportContainer.removeEventListener("touchend", handleTouchEnd);
      };
    }
  }, [isMobile, embedRef.current]);

  // 获取报表嵌入信息
  useEffect(() => {
    const getPowerBIData = async () => {
      setLoading(true);
      setError(null);
      try {
        const embedInfo = await getReportEmbedInfo(
          role,
          groupId,
          propsReportId,
          datasetId
        );
        setEmbedUrl(embedInfo.embedUrl);
        setReportId(embedInfo.reportId);
        setEmbedToken(embedInfo.embedToken);
      } catch (err) {
        setError(`获取报表信息失败: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };
    getPowerBIData();
  }, [retryCount]);

  // 根据当前的 isMobile 状态配置embedConfig
  const embedConfig = {
    type: "report",
    id: reportId,
    embedUrl: embedUrl,
    accessToken: embedToken,
    tokenType: models.TokenType.Embed,
    permissions: models.Permissions.Read,
    viewMode: currentViewMode, // 使用当前视图模式
    settings: {
      panes: {
        filters: {
          expanded: false,
          visible: !isMobile,
        },
        pageNavigation: {
          visible: true,
          position: models.PageNavigationPosition.Bottom,
        },
      },
      background: models.BackgroundType.Transparent,
      // 初始使用布局类型，之后会根据布局支持情况更新
      layoutType: isMobile
        ? models.LayoutType.MobilePortrait
        : models.LayoutType.Master,
      // 移动视图特有设置
      mobileLayout: {
        showPageNavigator: true,
        displayOption: models.DisplayOption.FitToWidth,
      },
      // 始终保持导航可见
      navbar: { visible: true },
      // 确保本地化设置正确
      localeSettings: { language: "zh-CN", formatLocale: "zh-CN" },
    },
    // 添加移动特定功能标志
    features: {
      mobile: true,
    },
  };

  // 添加页面切换事件处理
  const eventHandlers = new Map([
    [
      "loaded",
      async () => {
        console.log("报表加载完成");

        if (!embedRef.current || !embedRef.current.embed) return;

        try {
          // 获取所有页面
          const pages = await embedRef.current.embed.getPages();
          console.log(`报表共有 ${pages.length} 个页面`);
          setReportPages(pages);

          // 获取当前页面
          const activePage = await embedRef.current.embed.getActivePage();

          // 检查当前页面是否支持移动布局
          const mobileLayoutSupported = await activePage.hasLayout(
            models.LayoutType.MobilePortrait
          );
          console.log(
            `当前页面${mobileLayoutSupported ? "支持" : "不支持"}移动布局`
          );

          // 批量更新状态以减少渲染次数
          setHasMobileLayout(mobileLayoutSupported);
          setCurrentPage(activePage);

          // 计算应该使用的布局类型
          const targetLayoutType = isMobile
            ? mobileLayoutSupported
              ? models.LayoutType.MobilePortrait
              : models.LayoutType.MobileLandscape
            : models.LayoutType.Master;

          // 记录当前布局类型
          lastLayoutTypeRef.current = targetLayoutType;

          // 如果是移动设备但页面不支持移动布局，切换到移动横向布局
          if (isMobile && targetLayoutType !== models.LayoutType.Master) {
            console.log(
              `初始设置${
                targetLayoutType === models.LayoutType.MobilePortrait
                  ? "移动纵向"
                  : "移动横向"
              }布局`
            );
            await embedRef.current.embed.updateSettings({
              layoutType: targetLayoutType,
            });
          }

          // 完成布局检查 - 放在最后执行
          setLayoutCheckComplete(true);
        } catch (err) {
          console.error("报表初始化失败:", err);
          // 即使失败也标记布局检查完成，避免卡在加载状态
          setLayoutCheckComplete(true);
        }
      },
    ],
    ["rendered", () => console.log("报表渲染完成")],
    [
      "pageChanged",
      async (event) => {
        console.log("页面已切换:", event.detail);

        if (!embedRef.current || !embedRef.current.embed) return;

        try {
          // 获取新页面
          const newPage = await embedRef.current.embed.getActivePage();

          // 检查新页面是否支持移动布局
          const mobileLayoutSupported = await newPage.hasLayout(
            models.LayoutType.MobilePortrait
          );

          // 使用函数式更新防止潜在的闭包问题
          setCurrentPage(newPage);
          setHasMobileLayout(mobileLayoutSupported);

          // 如果是移动设备，根据布局支持情况更新视图
          if (isMobile) {
            const newLayoutType = mobileLayoutSupported
              ? models.LayoutType.MobilePortrait
              : models.LayoutType.MobileLandscape;

            // 检查是否需要更新布局类型
            if (lastLayoutTypeRef.current !== newLayoutType) {
              await embedRef.current.embed.updateSettings({
                layoutType: newLayoutType,
              });
              // 更新记录的布局类型
              lastLayoutTypeRef.current = newLayoutType;
            }
          }
        } catch (err) {
          console.error("页面切换后处理失败:", err);
        }
      },
    ],
    [
      "error",
      (event) => {
        console.error("Power BI 错误:", event?.detail);
        const errorDetail = event?.detail;
        if (!errorDetail) {
          setError("发生未知 Power BI 错误。");
          return;
        }
        if (
          errorDetail.message &&
          errorDetail.message.includes("TokenExpired")
        ) {
          setError("访问令牌已过期，将尝试自动更新。请重试。");
          setRetryCount((prev) => prev + 1);
        } else {
          setError(
            `报表加载错误: ${
              errorDetail.detailedMessage || errorDetail.message || "未知错误"
            }`
          );
        }
      },
    ],
  ]);

  if (loading) {
    return (
      <div className="centered-content-container">
        <div>正在加载 Power BI 报表...</div>
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (error || !embedUrl || !embedToken) {
    return (
      <div className="centered-content-container">
        <div className="error-message-box">
          <h3>加载报表时出错</h3>
          <p>{error || "未能获取报表嵌入信息。"}</p>
          <button
            onClick={() => setRetryCount((prev) => prev + 1)}
            className="retry-button"
          >
            重试加载
          </button>
        </div>
      </div>
    );
  }

  // 使用绝对定位方法来确保PowerBI报表填充整个可用空间
  return (
    <div
      style={{
        ...style,
        position: "relative",
        width: "100%",
        height: "100%",
        overflow: "hidden",
      }}
      className="report-container"
    >
      {/* 添加设备类型指示器，便于开发调试 */}
      {process.env.NODE_ENV === "development" && (
        <div
          style={{
            position: "absolute",
            top: 5,
            right: 5,
            zIndex: 1000,
            backgroundColor: "rgba(0,0,0,0.5)",
            color: "white",
            padding: "2px 5px",
            borderRadius: "3px",
            fontSize: "12px",
          }}
        >
          {isMobile
            ? hasMobileLayout
              ? "移动纵向视图"
              : "移动横向视图"
            : "桌面视图"}
        </div>
      )}

      {/* 移动视图下显示自定义页面导航 */}
      {isMobile && reportPages.length > 1 && (
        <div
          style={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 1000,
            padding: "5px 0",
            backgroundColor: "rgba(255,255,255,0.7)",
          }}
        >
          <div className="mobile-nav">
            {reportPages.map((page, index) => (
              <button
                key={page.name}
                className={`mobile-nav-button ${
                  currentPage && currentPage.name === page.name ? "active" : ""
                }`}
                onClick={() => {
                  if (embedRef.current && embedRef.current.embed) {
                    page
                      .setActive()
                      .catch((err) =>
                        console.error(`页面切换失败: ${err.message}`)
                      );
                  }
                }}
              >
                {page.displayName || `页面 ${index + 1}`}
              </button>
            ))}
          </div>
        </div>
      )}

      <PowerBIEmbed
        embedConfig={embedConfig}
        eventHandlers={eventHandlers}
        ref={embedRef}
        cssClassName="powerbi-embed-component"
      />
    </div>
  );
}

export default PowerBIReport;
